/* eslint-disable react-hooks/exhaustive-deps */
//* *********************************** REACT IMPORTS ************************************
import React, { useEffect, useState } from "react";

//* ********************************* EXTERNAL PACKAGES **********************************
import { useHistory, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

//* ************************************** REDUX *****************************************
import {
  getProtocolsList,
  getFrequencyList,
  createPatientProgram,
  getPatientProgramDetails,
  updateProgramProtocolFrequency,
} from "redux/actions/patientsPrograms";

//* ************************************** GLOBALS ***************************************
import bigBackButton from "assets/images/big-left-arrow.svg";
import deleteIcon from "assets/images/delete_base.svg";
import lock_input from "assets/images/lock-input.svg";
import { languageCodes } from "configs/i18n";
import NavBar from "components/navbar";
import Breadcrumbs from "components/breadcrumbs";
import Spinner from "components/spinner";
import MessageModal from "components/messageModal";

const CreateOrEditProgram = (props) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const { patient_id, program_id } = useParams();
  const isEditing = !!program_id; // If we have a program_id => we are editing

  // Redux slices of state
  const patientProtocols = useSelector((state) => state.patientProtocols);
  const patientProtocolsFrequency = useSelector(
    (state) => state.patientProtocolsFrequency
  );
  const patientProgramDetails = useSelector(
    (state) => state.patientProgramDetails
  );

  // Local state
  const [name, setName] = useState("");
  const [type, setType] = useState("");
  const [frequency, setFrequency] = useState("");
  const [protocols, setProtocols] = useState([]); // local store of protocols

  const [isSaving, setIsSaving] = useState(false);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [protocolIndexToDelete, setProtocolIndexToDelete] = useState(null);

  // i18n
  const { t, i18n } = useTranslation();

  // ------------------- Fetch Data on Mount -------------------
  useEffect(() => {
    const languageCode = languageCodes[i18n.language];
    dispatch(getProtocolsList(patient_id, languageCode)).catch((err) =>
      console.log("Error getProtocolsList:", err)
    );
    dispatch(getFrequencyList(patient_id, languageCode)).catch((err) =>
      console.log("Error getFrequencyList:", err)
    );

    if (isEditing) {
      dispatch(getPatientProgramDetails(patient_id, program_id)).catch((err) =>
        console.log("Error getPatientProgramDetails:", err)
      );
    }
  }, []);

  useEffect(() => {
    if (isEditing && patientProgramDetails) {
      setName(patientProgramDetails.program_name || "");
      // Convert existing protocols to a local array
      // Store "originalFrequencyId" to detect changes at save time
      const protocolsList = (patientProgramDetails.protocols || []).map(
        (p) => {
            const freqObj = patientProtocolsFrequency.find(
              (f) => f.description === p.protocol_frequency
            );
            const freqId = freqObj ? freqObj.id : "";

            return ({
            id: p.protocol_id,
            type: p.protocol_type_id,
            description: p.protocol_activity_name,
            frequencyId: freqId,
            frequencyDescription: p.protocol_frequency,
            originalFrequencyId: freqId,
          })
        }
      );
      protocolsList.sort((a, b) => a.id - b.id);
      setProtocols(protocolsList);
    }
  }, [isEditing, patientProgramDetails]);


  const addProtocol = () => {
    if (!type || !frequency) return;
    const [selectedId, selectedType] = type.split("-");

    // find the correct description for the chosen test/survey
    let protocolDescription = "";
    if (selectedType === "1") { // Test
      const found = patientProtocols.tests.find(
        (item) => `${item.id}` === selectedId
      );
      protocolDescription = found ? found.description : "";
    } else { // Survey
      const found = patientProtocols.surveys.find(
        (item) => `${item.id}` === selectedId
      );
      protocolDescription = found ? found.description : "";
    }

    const frequencyItem = patientProtocolsFrequency.find(
      (f) => `${f.id}` === frequency
    );
    const frequencyDescription = frequencyItem ? frequencyItem.description : "";

    const newProtocol = {
      id: selectedId,
      type: selectedType,
      description: protocolDescription,
      frequencyId: frequency,
      frequencyDescription,
      originalFrequencyId: frequency, // new in create
    };

    // If editing, we do NOT allow adding protocols => do nothing
    if (isEditing) return;

    // Otherwise, in create mode, we can add to local array
    setProtocols([...protocols, newProtocol]);

    // Reset selects
    setType("");
    setFrequency("");
  };

  const confirmDeleteProtocol = (index) => {
    setProtocolIndexToDelete(index);
    setDeleteConfirmModal(true);
  };

  const removeProtocol = () => {
    if (protocolIndexToDelete === null) return;

    // create mode => remove from local array
    const updatedArr = [...protocols];
    updatedArr.splice(protocolIndexToDelete, 1);
    setProtocols(updatedArr);

    setDeleteConfirmModal(false);
    setProtocolIndexToDelete(null);
  };

  const handleProtocolFrequencyChange = (index, newFreqId) => {
    const changedProtocol = protocols[index];
    changedProtocol.frequencyId = newFreqId;

    const freqObj = patientProtocolsFrequency.find(
      (f) => `${f.id}` === newFreqId
    );
    if (freqObj) {
      changedProtocol.frequencyDescription = freqObj.description;
    }

    const newArr = [...protocols];
    newArr[index] = changedProtocol;
    console.log(`setting protocols: ${JSON.stringify(newArr)}`)
    setProtocols(newArr);
  };

  const handleSave = async () => {
    setIsSaving(true);

    if (!isEditing) {
      const newProgramPayload = {
        name,
        protocols: protocols.map((p) => ({
          module_id: p.id,
          module_type_id: p.type,
          frequency_id: p.frequencyId,
        })),
      };
      dispatch(createPatientProgram(patient_id, newProgramPayload)).then(
        () => {
          setIsSaving(false);
          history.goBack();
        },
        (err) => {
          setIsSaving(false);
          console.log("Create program error:", err);
        }
      );
    } else {
      // We do not have a single "update program" API, so we must call
      // updateProgramProtocolFrequency for each protocol whose freq changed
      // from its originalFrequencyId.

      const changedProtocols = protocols.filter(
        (protocol) => protocol.frequencyId !== protocol.originalFrequencyId
      );
      // For each changed protocol, do an await on updateProgramProtocolFrequency
      try {
        for (const changedProtocol of changedProtocols) {
          await dispatch(
            updateProgramProtocolFrequency(
              patient_id,
              program_id,
              changedProtocol.id,
              changedProtocol.frequencyId
            )
          );
        }
        setIsSaving(false);
        history.goBack();
      } catch (err) {
        console.log("Error updating frequencies in edit mode:", err);
        setIsSaving(false);
      }
    }
  };

  // If editing but no details yet
  if (isEditing && !patientProgramDetails) {
    return <Spinner />;
  }

  const isSaveDisabled = () => {
    if (isSaving) return true;
    if (!isEditing) {
      if (!name.trim() || protocols.length === 0) return true;
    }
    return false;
  };

  const cancelEdit = () => {
    history.goBack();
  };

  return (
    <div className='col patient-container'>
      <div className='row-space-between'>
        <Breadcrumbs />
        <NavBar />
      </div>

      {/* Row with either program name input (create) or read-only name (edit)
          plus the Save button on the right */}
      <div className='title-container d-flex align-items-center justify-content-between'>
        <div className='d-flex align-items-center'>
          <img
            onClick={cancelEdit}
            alt='back Button'
            src={bigBackButton}
            style={{ cursor: "pointer", marginRight: "1rem" }}
          />
          <h4>
            {isEditing ? t("titles.edit_program") : t("titles.create_program")}
          </h4>
        </div>
      </div>

      <div className='programs-module-wrapper'>
        <div className='programs-module-container'>
          <div className='row-space-between'>
            <div className="col-5">
              <label className='d-flex justify-content-between' htmlFor='name'>
              <span className='input-label-text'>
                {t("patient.programs.program_name")}
              </span>
              </label>
              {isEditing ?
                <div className="input-image-wrapper image-right">
                  <input
                    disabled={true}
                    autoComplete='off'
                    name="name"
                    id="name"
                    className='form-input'
                    placeholder={t("placeholders.program_name")}
                    value={name}
                  />
                  <img
                    alt="Lock"
                    className="input-right-image default_cursor"
                    data-tip
                    data-for="name"
                    src={lock_input}
                  />
                </div> :
                <input
                  disabled={isEditing}
                  autoComplete='off'
                  name="name"
                  id="name"
                  className='form-input'
                  placeholder={t("placeholders.program_name")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              }
            </div>
            <div className='d-flex align-items-center'>
              <button
                onClick={cancelEdit}
                type='button'
                className='small-button transparent-button mr-3'
              >
                <u>{t("buttons.cancel")}</u>
              </button>
              <div className='input-image-wrapper m-0'>
                <button
                  onClick={handleSave}
                  disabled={isSaveDisabled()}
                  className='small-button secondary-button'
                >
                  {t("buttons.save")}
                </button>
                {isSaving && <Spinner type='small' />}
              </div>
            </div>
          </div>
          {/* Adding protocls */}
          {!isEditing && (
            <>
              <div className='row mt-4'>
                <div className='col-5'>
                  <label className='d-flex justify-content-between' htmlFor='type'>
                    <span className='input-label-text'>
                      {t("patient.programs.select_test_survey")}
                    </span>
                  </label>
                  <select
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    className={`w-100 dropdown ${type === "" && "notSelected"}`}
                    name='type'
                    id='type'
                  >
                    <option value=''>{t("placeholders.option")}</option>
                    <optgroup label='Surveys'>
                      {patientProtocols.surveys.map((item) => (
                        <option key={`${item.id}-2`} value={`${item.id}-2`}>
                          {item.description}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label='Tests'>
                      {patientProtocols.tests.map((item) => (
                        <option key={`${item.id}-1`} value={`${item.id}-1`}>
                          {item.description}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                </div>

                <div className='col-5'>
                  <label className='d-flex justify-content-between' htmlFor='frequency'>
                    <span className='input-label-text'>
                      {t("patient.programs.select_frequency")}
                    </span>
                  </label>
                  <select
                    value={frequency}
                    onChange={(e) => setFrequency(e.target.value)}
                    className={`w-100 dropdown ${frequency === "" && "notSelected"}`}
                    name='frequency'
                    id='frequency'
                  >
                    <option value=''>{t("placeholders.option")}</option>
                    {patientProtocolsFrequency.map((freq) => (
                      <option key={freq.id} value={freq.id}>
                        {freq.description}
                      </option>
                    ))}
                  </select>
                </div>

                <div className='col d-flex align-items-end'>
                  <button
                    onClick={addProtocol}
                    disabled={frequency === "" || type === ""}
                    className='button justify-content-center small-wider-button tertiary-button flex-fill'
                    style={{ marginBottom: "24px" }}
                  >
                    {t("buttons.add_survey_test")}
                  </button>
                </div>
              </div>
            </>
          )}

          <div className='row mt-3'>
            <span className='input-label-text'>
              {t("patient.programs.added_modules")}
            </span>
          </div>
          <div className='mt-2'>
            {protocols.map((protocol, index) => (
              <div key={index} className='row added-module-info'>
                <div className='col-5'>
                  <div className='regular-text black-color'>
                    {protocol.description}
                  </div>
                </div>
                <div className='col-auto'>
                  <div className='regular-text'>|</div>
                </div>
                <div className='col-4 align-items-center'>
                  {/* If editing => frequency dropdown, else just show freq desc */}
                  {isEditing ? (
                    <select
                      value={protocol.frequencyId}
                      onChange={(e) => {
                        handleProtocolFrequencyChange(index, e.target.value)
                      }
                      }
                      className='dropdown w-100'
                    >
                      {patientProtocolsFrequency.map((freq) => (
                        <option key={freq.id} value={freq.id}>
                          {freq.description}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className='regular-text'>
                      {protocol.frequencyDescription}
                    </div>
                  )}
                </div>

                {!isEditing && (
                  <div className='col-auto mr-1'>
                    <img
                      alt='Delete Module'
                      src={deleteIcon}
                      onClick={() => confirmDeleteProtocol(index)}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Delete protocol confirm */}
      {deleteConfirmModal && (
        <MessageModal
          title={t("titles.confirm_delete")}
          message={t("messages.delete_protocol_confirmation")}
          confirmButtonText={t("buttons.delete")}
          cancelButtonText={t("buttons.cancel")}
          onConfirm={removeProtocol}
          onCancel={() => {
            setDeleteConfirmModal(false);
            setProtocolIndexToDelete(null);
          }}
        />
      )}
    </div>
  );
};

export default React.memo(CreateOrEditProgram);
